import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

function formatError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => (e && e.msg) || JSON.stringify(e)).join(" ");
  if (detail.msg) return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { checkAuth(); }, []);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (e) {
      // 403 = suspended account — force logout
      if (e.response?.status === 403) {
        sessionStorage.removeItem("bompay_unlocked");
      }
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, prefetchedData = null) => {
    try {
      // If prefetched data provided (phone login already completed), just set user
      if (prefetchedData && password === "__phone_login__") {
        setUser(prefetchedData);
        sessionStorage.setItem("bompay_unlocked", "1");
        return { success: true, data: prefetchedData };
      }
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
      sessionStorage.setItem("bompay_unlocked", "1");
      return { success: true, data };
    } catch (e) {
      return { success: false, error: formatError(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (userData) => {
    try {
      const { data } = await axios.post(`${API}/auth/register`, userData, { withCredentials: true });
      setUser(data);
      sessionStorage.setItem("bompay_unlocked", "1");
      return { success: true, data };
    } catch (e) {
      return { success: false, error: formatError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    sessionStorage.removeItem("bompay_unlocked");
    setUser(false);
  };

  const updateUser = (updates) => setUser(prev => ({ ...prev, ...updates }));

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, register, updateUser, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export { API };
